import React, { useState, useEffect, useMemo } from 'react';
import { Clock, LayoutDashboard, Activity, Target, Scale, TrendingDown, TrendingUp, TrendingDown as TrendDown } from 'lucide-react';
import { fetchTradesFromDB } from '../journaling/JournalingService';
import { getPerformanceAnalytics } from '../../services/performanceAnalytics';
import { trackPerformanceFilterChanged } from '../../services/analytics';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import AnalyticsDashboard from './AnalyticsDashboard';
import PatternRecognition from './widgets/PatternRecognition';
import SessionPanel from './widgets/SessionPanel';

const Card = ({ children, className = '' }) => (
  <div className={`bg-card text-card-foreground rounded-xl shadow-sm border border-border ${className}`}>{children}</div>
);

const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <span className="h-3.5 w-0.5 rounded-full bg-primary inline-block" />
    <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{children}</span>
  </div>
);

const filterTradesByTimeRange = (trades, timeRange) => {
  if (timeRange === 'ALL') return trades;

  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return trades.filter((trade) => {
    const tradeDate = new Date(trade.trade_date || trade.closeTime);
    if (isNaN(tradeDate.getTime())) return true;

    switch (timeRange) {
      case 'TODAY':
        return tradeDate >= startOfDay;
      case 'WEEK': {
        const startOfWeek = new Date(startOfDay);
        startOfWeek.setDate(startOfDay.getDate() - startOfDay.getDay());
        return tradeDate >= startOfWeek;
      }
      case 'MONTH': {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return tradeDate >= startOfMonth;
      }
      default:
        return true;
    }
  });
};

const Analytics = () => {
  const [allTrades, setAllTrades] = useState([]);
  const [timeRange, setTimeRange] = useState('ALL');
  const [viewMode, setViewMode] = useState('charts');
  const [patternData, setPatternData] = useState({});
  const [stats, setStats] = useState({
    totalTrades: 0,
    totalProfit: 0,
    winRate: 0,
    averagePips: 0,
    bestTrade: 0,
    worstTrade: 0,
    averageWin: 0,
    averageLoss: 0,
    expectancy: 0,
    profitFactor: 0,
    maxDrawdown: 0,
    maxDrawdownPercent: 0
  });

  useEffect(() => {
    loadTrades();
    loadPatternData();
  }, []);

  const loadTrades = async () => {
    try {
      const tradesData = await fetchTradesFromDB();
      setAllTrades(tradesData);
    } catch (error) {
      console.error('Error loading trades:', error);
    }
  };

  const loadPatternData = async () => {
    try {
      const result = await getPerformanceAnalytics(false);
      if (result?.success) {
        setPatternData(result.pattern_recognition || {});
      }
    } catch (error) {
      console.error('Error loading pattern data:', error);
    }
  };

  const trades = useMemo(() => {
    return filterTradesByTimeRange(allTrades, timeRange);
  }, [allTrades, timeRange]);

  useEffect(() => {
    calculateStats(trades);
  }, [trades]);

  const calculateStats = (tradesData) => {
    const totalTrades = tradesData.length;
    const totalProfit = tradesData.reduce((sum, trade) => sum + (Number(trade.profit) || 0), 0);
    const winningTrades = tradesData.filter(trade => Number(trade.profit) > 0);
    const losingTrades = tradesData.filter(trade => Number(trade.profit) < 0);
    const totalPips = tradesData.reduce((sum, trade) => sum + (Number(trade.pips) || 0), 0);

    const profits = tradesData.map(trade => Number(trade.profit) || 0);

    const winRate = totalTrades > 0 ? winningTrades.length / totalTrades : 0;
    const lossRate = totalTrades > 0 ? losingTrades.length / totalTrades : 0;
    const averageWin = winningTrades.length > 0
      ? winningTrades.reduce((sum, trade) => sum + (Number(trade.profit) || 0), 0) / winningTrades.length
      : 0;
    const averageLoss = losingTrades.length > 0
      ? Math.abs(losingTrades.reduce((sum, trade) => sum + (Number(trade.profit) || 0), 0) / losingTrades.length)
      : 0;

    const expectancy = (winRate * averageWin) - (lossRate * averageLoss);

    const grossProfit = winningTrades.reduce((sum, t) => sum + (Number(t.profit) || 0), 0);
    const grossLoss = Math.abs(losingTrades.reduce((sum, t) => sum + (Number(t.profit) || 0), 0));
    const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0;

    let maxDrawdown = 0;
    let maxDrawdownPercent = 0;
    if (tradesData.length > 0) {
      const sorted = [...tradesData].sort(
        (a, b) => new Date(a.trade_date || a.closeTime).getTime() - new Date(b.trade_date || b.closeTime).getTime()
      );
      let equity = 0;
      let peak = 0;
      for (const trade of sorted) {
        equity += Number(trade.profit) || 0;
        if (equity > peak) peak = equity;
        const drawdown = peak - equity;
        if (drawdown > maxDrawdown) {
          maxDrawdown = drawdown;
          maxDrawdownPercent = peak > 0 ? (drawdown / peak) * 100 : 0;
        }
      }
    }

    setStats({
      totalTrades,
      totalProfit,
      winRate: winRate * 100,
      averagePips: totalTrades > 0 ? totalPips / totalTrades : 0,
      bestTrade: profits.length > 0 ? Math.max(...profits) : 0,
      worstTrade: profits.length > 0 ? Math.min(...profits) : 0,
      averageWin,
      averageLoss: losingTrades.length > 0
        ? losingTrades.reduce((sum, trade) => sum + (Number(trade.profit) || 0), 0) / losingTrades.length
        : 0,
      expectancy,
      profitFactor,
      maxDrawdown,
      maxDrawdownPercent
    });
  };

  const VIEW_MODES = [
    { id: 'charts',   label: 'Charts',   Icon: LayoutDashboard },
    { id: 'session',  label: 'Session',  Icon: Clock },
    { id: 'patterns', label: 'Patterns', Icon: Activity },
  ];

  const expectancyColor = stats.totalTrades === 0
    ? 'text-muted-foreground'
    : stats.expectancy > 0 ? 'text-[#4ADE80]' : 'text-[#F87171]';

  const profitFactorColor = stats.totalTrades === 0
    ? 'text-muted-foreground'
    : stats.profitFactor === Infinity ? 'text-[#4ADE80]'
    : stats.profitFactor >= 2 ? 'text-[#4ADE80]'
    : stats.profitFactor >= 1.5 ? 'text-[#4ADE80]'
    : stats.profitFactor >= 1 ? 'text-yellow-400'
    : 'text-[#F87171]';

  return (
    <div className="pt-16 min-h-screen bg-background text-foreground">
      {/* Sticky header */}
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-16 z-10">
        <div className="px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <h1 className="text-xl font-semibold tracking-tight">Edge</h1>
          <div className="flex items-center gap-3">
            {/* View mode toggle */}
            <div className="flex items-center bg-secondary rounded-lg p-1 gap-0.5">
              {VIEW_MODES.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  onClick={() => setViewMode(id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150 ${
                    viewMode === id
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>

            {/* Time range */}
            <Select value={timeRange} onValueChange={(val) => { setTimeRange(val); trackPerformanceFilterChanged({ filter_type: 'time_range', value: val }); }}>
              <SelectTrigger className="w-[130px] bg-secondary border-border text-sm">
                <SelectValue placeholder="Time range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Time</SelectItem>
                <SelectItem value="TODAY">Today</SelectItem>
                <SelectItem value="WEEK">This Week</SelectItem>
                <SelectItem value="MONTH">This Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-10">

        {/* ── Edge Metrics ── */}
        <section>
          <SectionLabel>Edge Metrics</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

            {/* Expectancy */}
            <Card className="flex items-center gap-4 p-5">
              <div className={`p-2.5 rounded-lg flex-shrink-0 ${stats.totalTrades > 0 && stats.expectancy > 0 ? 'bg-[rgba(34,197,94,0.10)]' : 'bg-secondary'}`}>
                <Target className={`w-4 h-4 ${stats.totalTrades > 0 && stats.expectancy > 0 ? 'text-[#4ADE80]' : 'text-muted-foreground'}`} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Expectancy</p>
                <p className={`text-2xl font-bold font-mono tabular-nums tracking-tight leading-none ${expectancyColor}`}>
                  {stats.totalTrades === 0 ? '—' : `${stats.expectancy >= 0 ? '+' : ''}$${stats.expectancy.toFixed(2)}`}
                </p>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {stats.totalTrades === 0 ? 'No trades yet' : stats.expectancy > 0 ? 'Positive edge' : 'Negative edge'}
                </p>
              </div>
            </Card>

            {/* Profit Factor */}
            <Card className="flex items-center gap-4 p-5">
              <div className={`p-2.5 rounded-lg flex-shrink-0 ${stats.totalTrades > 0 && stats.profitFactor >= 1.5 ? 'bg-[rgba(34,197,94,0.10)]' : stats.totalTrades > 0 && stats.profitFactor >= 1 ? 'bg-[rgba(234,179,8,0.10)]' : 'bg-secondary'}`}>
                <Scale className={`w-4 h-4 ${profitFactorColor}`} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Profit Factor</p>
                <p className={`text-2xl font-bold font-mono tabular-nums tracking-tight leading-none ${profitFactorColor}`}>
                  {stats.totalTrades === 0 ? '—' : stats.profitFactor === Infinity ? '∞' : stats.profitFactor.toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {stats.totalTrades === 0 ? 'No trades yet'
                    : stats.profitFactor === Infinity ? 'No losses'
                    : stats.profitFactor >= 2 ? 'Strong'
                    : stats.profitFactor >= 1.5 ? 'Solid'
                    : stats.profitFactor >= 1 ? 'Weak edge'
                    : 'Losing system'}
                </p>
              </div>
            </Card>

            {/* Max Drawdown */}
            <Card className="flex items-center gap-4 p-5">
              <div className={`p-2.5 rounded-lg flex-shrink-0 ${stats.totalTrades > 0 ? 'bg-[rgba(239,68,68,0.10)]' : 'bg-secondary'}`}>
                <TrendDown className={`w-4 h-4 ${stats.totalTrades > 0 ? 'text-[#F87171]' : 'text-muted-foreground'}`} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Max Drawdown</p>
                <p className={`text-2xl font-bold font-mono tabular-nums tracking-tight leading-none ${stats.totalTrades === 0 ? 'text-muted-foreground' : 'text-[#F87171]'}`}>
                  {stats.totalTrades === 0 ? '—' : `-$${stats.maxDrawdown.toFixed(2)}`}
                </p>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {stats.totalTrades === 0 ? 'No trades yet'
                    : stats.maxDrawdown === 0 ? 'No drawdown'
                    : `${stats.maxDrawdownPercent.toFixed(1)}% from peak`}
                </p>
              </div>
            </Card>

          </div>
        </section>

        {/* ── Charts view ── */}
        {viewMode === 'charts' && (
          <section>
            <SectionLabel>Charts</SectionLabel>
            <AnalyticsDashboard trades={trades} />
          </section>
        )}

        {/* ── Session view ── */}
        {viewMode === 'session' && (
          <section>
            <SessionPanel trades={trades} />
          </section>
        )}

        {/* ── Patterns view ── */}
        {viewMode === 'patterns' && (
          <div className="space-y-8">
            <section>
              <SectionLabel>Trade Extremes</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Best Trade',   value: `+$${stats.bestTrade.toFixed(2)}`,      color: 'text-[#4ADE80]', bg: 'bg-[rgba(34,197,94,0.10)]', icon: <TrendingUp className="w-4 h-4 text-[#4ADE80]" /> },
                  { label: 'Worst Trade',  value: `$${stats.worstTrade.toFixed(2)}`,      color: 'text-[#F87171]', bg: 'bg-[rgba(239,68,68,0.10)]', icon: <TrendingDown className="w-4 h-4 text-[#F87171]" /> },
                  { label: 'Avg Win',      value: `+$${stats.averageWin.toFixed(2)}`,     color: 'text-[#4ADE80]', bg: 'bg-[rgba(34,197,94,0.10)]', icon: <TrendingUp className="w-4 h-4 text-[#4ADE80]" /> },
                  { label: 'Avg Loss',     value: `$${Math.abs(stats.averageLoss).toFixed(2)}`, color: 'text-[#F87171]', bg: 'bg-[rgba(239,68,68,0.10)]', icon: <TrendingDown className="w-4 h-4 text-[#F87171]" /> },
                ].map(({ label, value, color, bg, icon }) => (
                  <Card key={label} className="flex items-center gap-3 p-4">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${bg}`}>{icon}</div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{label}</p>
                      <p className={`text-lg font-bold tabular-nums ${color}`}>{value}</p>
                    </div>
                  </Card>
                ))}
              </div>
            </section>

            <section>
              <SectionLabel>Trade Breakdown</SectionLabel>
              <Card className="p-5">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">Winning</p>
                    <p className="text-2xl font-bold text-green-400 tabular-nums">
                      {trades.filter(t => Number(t.profit) > 0).length}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">Losing</p>
                    <p className="text-2xl font-bold text-red-400 tabular-nums">
                      {trades.filter(t => Number(t.profit) < 0).length}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">Breakeven</p>
                    <p className="text-2xl font-bold text-muted-foreground tabular-nums">
                      {trades.filter(t => Number(t.profit) === 0).length}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">Win Rate</p>
                    <p className={`text-2xl font-bold font-mono tabular-nums ${stats.winRate >= 50 ? 'text-[#4ADE80]' : 'text-[#F87171]'}`}>
                      {stats.winRate.toFixed(1)}%
                    </p>
                  </div>
                </div>
              </Card>
            </section>

            <section>
              <SectionLabel>Pattern Recognition</SectionLabel>
              <PatternRecognition data={patternData} />
            </section>
          </div>
        )}

      </div>
    </div>
  );
};

export default Analytics;
